import { defineStore } from 'pinia';
import { computed, ref, nextTick } from 'vue';
import { NavItem } from 'epubjs/types/navigation';
import { PageListItem } from 'epubjs/types/pagelist';
import Section from 'epubjs/types/section';

import {
  buildChildParentNavigationDependency,
  buildChildParentPageNavigationDependency,
} from '../util/navigation-controls-helper';
import type { EnhancedPage, EnhancedTocItem, LoiItem } from '../types/navigation';
import type { Manifest } from '../util/manifest';
import { parseLoi } from '../util/loi';
import { getSmilPathForItemByHref } from '../util/smil';
import { EpubJsBook, EpubJsSpine, EpubJsSpineItem } from '../types/epubjs';
import {
  flattenToc,
  searchToc,
  extractNavigationLevels,
  buildTocLookupMap,
  countTocItems,
} from '../util/navigation/toc-utils';
import { buildPageLookupMap } from '../util/navigation/page-utils';

/**
 * NavigationStructureStore
 *
 * Manages the book's navigation structure (ToC and Pages) without any state about the current position.
 * This is a pure data store - no position tracking, no UI state.
 *
 * Level 0 in the dependency hierarchy - has no dependencies on other stores.
 */
const useNavigationStructureStore = defineStore('book-player-navigation-structure', () => {
  const enhancedToc = ref<EnhancedTocItem[]>([]);
  const pages = ref<EnhancedPage[]>([]);
  const loi = ref<LoiItem[]>([]);

  const hasLoi = computed(() => loi.value.length > 0);

  const tocByHref = ref<Record<string, EnhancedTocItem>>({});
  const pagesByHref = ref<Record<string, EnhancedPage>>({});
  const pageToTocMap = ref<Map<string, string>>(new Map());
  const tocDependencyMap = ref<Map<string, string>>(new Map());

  const tocFeature = ref<boolean>(true);
  const annotationFeature = ref<boolean>(true);

  const isInitializing = ref(false);
  const resolutionProgress = ref(0);

  const hasTableOfContents = computed(() => tocFeature.value && enhancedToc.value.length > 0);
  const hasPages = computed(() => pages.value.length > 0);
  const hasAnnotations = computed(() => annotationFeature.value);
  const totalPages = computed(() => pages.value.length);

  // Use utility functions for computed properties
  const navigationLevels = computed(() => extractNavigationLevels(enhancedToc.value));
  const flattenedToc = computed(() => flattenToc(enhancedToc.value));

  const firstAndLastPageNumber = computed(() => {
    // Check if the array is empty
    if (!pages.value || pages.value.length === 0) {
      return { min: 1, max: 1 };
    }

    // Filter out any potential NaNs
    const pageNumbers = pages.value.map((p) => Number(p.page)).filter((n) => !isNaN(n));

    // Check if filtering emptied the array
    if (pageNumbers.length === 0) {
      return { min: 1, max: 1 };
    }

    return {
      min: Math.min(...pageNumbers),
      max: Math.max(...pageNumbers),
    };
  });

  const CHUNK_SIZE = 15;

  const initializeNavigation = async (
    m: Manifest,
    b: EpubJsBook,
    getElementCFI: (href: string, id: string) => Promise<string>,
  ) => {
    isInitializing.value = true;
    resolutionProgress.value = 0;

    // Calculate total work units for a meaningful progress bar
    const totalTocItems = countTocItems(m.toc || []);
    const totalPageItems = m.pages?.length || 0;
    const totalWorkUnits = totalTocItems + totalPageItems;
    let unitsCompleted = 0;

    // Internal helper to update progress globally
    const tickProgress = (amount = 1) => {
      unitsCompleted += amount;
      if (totalWorkUnits > 0) {
        resolutionProgress.value = Math.round((unitsCompleted / totalWorkUnits) * 100);
      }
    };

    try {
      if (m.toc && m.toc.length > 0) {
        enhancedToc.value = await processTocItems(m.toc, b, getElementCFI, tickProgress);
        // Use utility function to build a lookup map
        tocByHref.value = buildTocLookupMap(enhancedToc.value);
      }

      if (m.pages && m.pages.length > 0) {
        const processedPages = await processPageList(m.pages, b, getElementCFI, tickProgress);
        pages.value = processedPages;
        // Use utility function to build a page lookup map
        pagesByHref.value = buildPageLookupMap(processedPages);
      }

      if (enhancedToc.value.length > 0 && pages.value.length > 0) {
        tocDependencyMap.value = buildChildParentNavigationDependency(m.toc!, pages.value, 0);
        pageToTocMap.value = buildChildParentPageNavigationDependency(m.toc!, pages.value, (id?: string | number) => {
          if (!b.spine) {
            throw new Error('Book spine is not initialized');
          }
          return b.spine.get(id) as Section;
        });
      }
    } catch (error) {
      console.error('Failed to initialize book navigation:', error);
    } finally {
      resolutionProgress.value = 100;
      isInitializing.value = false;
    }
  };

  async function getCfiFromHref(href: string, book: EpubJsBook, getCFI: (h: string, i: string) => Promise<string>) {
    const [rawHref, id] = href.split('#');
    let cfi: string | undefined;
    let cfiBase: string | undefined;

    const correctedHref = resolveHrefInSpine(book, rawHref);

    try {
      cfi = await getCFI(correctedHref, id || '');
      cfiBase = cfi.replace(/^epubcfi\(/, '').replace(/\)$/, '');
    } catch (e) {
      console.error(`CFI error for ${correctedHref}`, e);
    }
    return { cfi, cfiBase, correctedHref };
  }

  const processTocItems = async (
    items: NavItem[],
    book: EpubJsBook,
    getCFI: (h: string, i: string) => Promise<string>,
    onItemProcessed: () => void,
  ): Promise<EnhancedTocItem[]> => {
    const results: EnhancedTocItem[] = [];

    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      const chunk = items.slice(i, i + CHUNK_SIZE);
      const resolved = await Promise.all(
        chunk.map(async (item) => {
          const { cfi, cfiBase, correctedHref } = await getCfiFromHref(item.href, book, getCFI);
          onItemProcessed(); // Update progress for each item processed

          return {
            ...item,
            href: correctedHref,
            cfi,
            cfiBase,
            subitems: item.subitems?.length
              ? await processTocItems(item.subitems, book, getCFI, onItemProcessed)
              : undefined,
          } as EnhancedTocItem;
        }),
      );

      results.push(...resolved);
      await nextTick();
    }
    return results;
  };

  const processPageList = async (
    items: PageListItem[],
    book: EpubJsBook,
    getCFI: (h: string, i: string) => Promise<string>,
    onItemProcessed: (amount: number) => void,
  ): Promise<EnhancedPage[]> => {
    const spineItems = (book.spine as EpubJsSpine).spineItems as EpubJsSpineItem[];
    const refined: EnhancedPage[] = [];

    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      const chunk = items.slice(i, i + CHUNK_SIZE);
      const resolved = await Promise.all(
        chunk.map(async (p) => {
          const [, id] = p.href.split('#');
          const { cfi, cfiBase, correctedHref } = await getCfiFromHref(p.href, book, getCFI);
          const smilPath = getSmilPathForItemByHref(correctedHref, spineItems);

          return {
            href: correctedHref,
            page: Number(p.page),
            id: id || undefined,
            cfi,
            cfiBase,
            smilPath: smilPath || undefined,
          } as EnhancedPage;
        }),
      );

      refined.push(...resolved);
      onItemProcessed(chunk.length);
      await nextTick();
    }

    return refined.sort((a, b) => {
      if (a.page !== b.page) {
        return a.page - b.page;
      }

      if (!a.cfi || !b.cfi) return 0;
      try {
        // @ts-ignore
        const compare = book.rendition?.epubcfi?.compare || book.epubcfi?.compare;
        if (compare) {
          return compare(a.cfi, b.cfi);
        }
      } catch (e) {
        console.error('Error comparing CFIs for page sorting:', e);
        return 0;
      }
      return 0;
    });
  };

  const resolveHrefInSpine = (book: EpubJsBook, partialHref: string): string => {
    if (!book.spine || !book.spine.spineItems) return partialHref;

    if (book.spine.get(partialHref)) return partialHref;

    const normalizedPartial = partialHref.startsWith('/') ? partialHref : `/${partialHref}`;
    const match = book.spine.spineItems.find(
      (item: EpubJsSpineItem) => (item.href && item.href.endsWith(normalizedPartial)) || item.href === partialHref,
    );

    if (match && match.href) {
      return match.href;
    }

    return partialHref;
  };

  const setHasTableOfContent = (features: boolean) => {
    tocFeature.value = features;
  };

  const setHasAnnotations = (features: boolean) => {
    annotationFeature.value = features;
  };

  const findInNavigation = (search: string, flatten: boolean = false): EnhancedTocItem[] => {
    if (!search.trim()) return enhancedToc.value;

    if (flatten) {
      // For flattened search, filter the flattened list
      return flattenedToc.value.filter((item) => item.label?.toLowerCase().includes(search.toLowerCase()));
    }

    // Use utility function for tree search
    return searchToc(enhancedToc.value, search);
  };

  const getTocByHref = (href: string) => tocByHref.value[href];

  const getPageByHref = (href: string) => pagesByHref.value[href];

  const getActiveTocHrefByHref = (href: string) => tocDependencyMap.value.get(href);

  const getChapterTitleForHref = (href: string | null) => {
    if (!href) return '';

    // Direct O(1) lookup - works for both base hrefs and hrefs with fragments (ids)
    if (tocByHref.value[href]) {
      return tocByHref.value[href].label || '';
    }

    // Fall back: try base href if the original had a fragment
    const [baseHref] = href.split('#');
    if (baseHref !== href && tocByHref.value[baseHref]) {
      return tocByHref.value[baseHref].label || '';
    }

    // Last resort: use parent relationship (via tocDependencyMap)
    const tocHref = getActiveTocHrefByHref(href);
    if (tocHref && tocByHref.value[tocHref]) {
      return tocByHref.value[tocHref].label || '';
    }

    return '';
  };
  const getChapterTitleForPageIndex = (index: number) => {
    const page = pages.value[index];
    if (!page) return '';
    return getChapterTitleForHref(page.href);
  };

  // Mirrors the rendition spread layout:
  //   spine[0]          → cover, alone on right (no spread class)
  //   spine[odd]        → left side of spread
  //   spine[even > 0]   → right side of spread
  const markSpreadPairs = (items: LoiItem[]): LoiItem[] =>
    items.map((item) => ({
      ...item,
      isSpreadPairLeft: item.spineIndex % 2 === 1,
      isSpreadPairRight: item.spineIndex > 0 && item.spineIndex % 2 === 0,
    }));

  const initializeLoi = async (b: EpubJsBook, spineItems: Manifest['spineItems']): Promise<void> => {
    try {
      const rawItems = await parseLoi(b);
      const spineByPath = Object.values(spineItems);
      const resolved = rawItems.map((item) => ({
        ...item,
        spineIndex:
          spineByPath.find(
            (s) => s.path === item.href || s.path.endsWith(`/${item.href}`) || item.href.endsWith(s.path),
          )?.index ?? -1,
        isSpreadPairLeft: false,
        isSpreadPairRight: false,
      }));

      loi.value = markSpreadPairs(resolved);
    } catch {
      loi.value = [];
    }
  };

  const $reset = () => {
    enhancedToc.value = [];
    pages.value = [];
    loi.value = [];
    tocByHref.value = {};
    pagesByHref.value = {};
    pageToTocMap.value = new Map();
    tocDependencyMap.value = new Map();
    isInitializing.value = false;
    resolutionProgress.value = 0;
    tocFeature.value = true;
  };

  return {
    enhancedToc,
    pages,
    tocByHref,
    pagesByHref,
    navigationLevels,
    isInitializing,
    resolutionProgress,
    totalPages,
    tocDependencyMap,
    pageToTocMap,
    flattenedToc,
    hasTableOfContents,
    hasPages,
    hasLoi,
    hasAnnotations,
    firstAndLastPageNumber,
    loi,
    initializeNavigation,
    initializeLoi,
    setHasTableOfContent,
    setHasAnnotations,
    findInNavigation,
    getTocByHref,
    getPageByHref,
    getActiveTocHrefByHref,
    getChapterTitleForHref,
    getChapterTitleForPageIndex,
    $reset,
  };
});

export default useNavigationStructureStore;
